/**
 * Builds the entity data endpoint URL for the specified entity data type.
 * This URL can be used for entity data related operations:
 * - fetch a list of entities
 * - create/update/delete/etc. an entity
 * - fetch entity actions
 * - etc.
 *
 * @param {number | string} entityTypeIdOrName
 * The ID or name of the entity data type.
 *
 * @returns {string}
 * The correct data endpoint URL depending on whether a numeric ID or name string was specified.
 * - `/api/data/{id}` for numeric IDs (legacy)
 * - `/api/data/v2/{name}` for entity type names
 */
export const getDataEndpointUrl = (entityTypeIdOrName) => {
    const entityTypeId = parseInt(entityTypeIdOrName, 10);
    return typeof entityTypeId === 'number' && !Number.isNaN(entityTypeId)
        ? `/api/data/${entityTypeIdOrName}`
        : `/api/data/v2/${entityTypeIdOrName}`;
};

/**
 * Builds the data query endpoint URL for the specified entity data type.
 * This URL can be used to query entities of the specified entity data type.
 *
 * @param {number | string} entityTypeIdOrName
 * The ID or name of the entity data type.
 *
 * @returns
 * The correct data query endpoint URL depending on whether a numeric ID or name string was specified.
 * - `/api/data/{id}/query` for numeric IDs (legacy)
 * - `/api/data/v2/{name}/query` for entity type names
 */
export const getDataQueryEndpointUrl = (entityTypeIdOrName) => {
    const entityTypeId = parseInt(entityTypeIdOrName, 10);
    return typeof entityTypeId === 'number' && !Number.isNaN(entityTypeId)
        ? `/api/data/${entityTypeIdOrName}/query`
        : `/api/data/v2/${entityTypeIdOrName}/query`;
};

/**
 * Builds the data type configuration endpoint URL for the specified data type.
 * This URL can be used for data type configuration operations:
 * - fetch data type config
 * - fetch enum options of an enum data type
 * - etc.
 *
 * @param {number | string} dataTypeIdOrName
 * The ID or name of the data type.
 *
 * @returns {string}
 * The correct data type configuration endpoint URL depending on whether a numeric ID or name string was specified.
 * - `/api/config/data-types/{id}` for numeric IDs (legacy)
 * - `/api/config/data-types/v2/{name}` for data type names
 */
export const getDataTypeEndpointUrl = (dataTypeIdOrName) => {
    const dataTypeId = parseInt(dataTypeIdOrName, 10);
    return typeof dataTypeId === 'number' && !Number.isNaN(dataTypeId)
        ? `/api/config/data-types/${dataTypeIdOrName}`
        : `/api/config/v2/data-types/${dataTypeIdOrName}`;
};

/**
 * Converts names of datatypes for them to be safe in URLs
 * NOTE! Uses the same logic as the backend when creating the sitemap (when changing, change backend Gateway as well)
 * @param {string} entityTypeName name of the entity/dataType
 * @returns {string} URL-friendly version of the dataType name
 */
export const makeSafeForUrl = (entityTypeName) => {
    let safeUrl = entityTypeName.replace(/[^A-z0-9_]+/g, '-');
    if (safeUrl.slice(-1) === '-') {
        return safeUrl.slice(0, -1);
    } else {
        return safeUrl;
    }
};
